import clsx from 'clsx'
import { forwardRef } from 'react'

const colors = {
  primary: 'bg-primary-500 dark:bg-primary-400',
  red: 'bg-red-500 dark:bg-red-400',
  orange: 'bg-orange-500 dark:bg-orange-400',
  amber: 'bg-amber-500 dark:bg-amber-400',
  yellow: 'bg-yellow-500 dark:bg-yellow-300',
  lime: 'bg-lime-500 dark:bg-lime-300',
  green: 'bg-green-500 dark:bg-green-400',
  emerald: 'bg-emerald-500 dark:bg-emerald-400',
  teal: 'bg-teal-500 dark:bg-teal-300',
  cyan: 'bg-cyan-500 dark:bg-cyan-300',
  sky: 'bg-sky-500 dark:bg-sky-300',
  blue: 'bg-blue-500 dark:bg-blue-400',
  indigo: 'bg-indigo-500 dark:bg-indigo-400',
  violet: 'bg-violet-500 dark:bg-violet-400',
  purple: 'bg-purple-500 dark:bg-purple-400',
  fuchsia: 'bg-fuchsia-500 dark:bg-fuchsia-400',
  pink: 'bg-pink-500 dark:bg-pink-400',
  rose: 'bg-rose-500 dark:bg-rose-400',
  gray: 'bg-gray-500 dark:bg-gray-400',
}

const sizes = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
  xl: 'h-4',
}

type ProgressBarProps = {
  /** Progress value between 0 and 100 */
  value: number
  /** Color variant for the progress bar */
  color?: keyof typeof colors
  /** Size variant for the progress bar */
  size?: keyof typeof sizes
  /** Label content for the left side (can be string or JSX) */
  label?: React.ReactNode
  /** Whether the progress bar is animated */
  animated?: boolean
  /** Custom className for the container */
  className?: string
}

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, color = 'primary', size = 'md', label, animated = false, className, ...props }, ref) => {
    // Clamp value between 0 and 100
    const clampedValue = Math.max(0, Math.min(100, value))

    // Use Math.floor with multiplier to show 1 decimal place and prevent rounding up to 100%
    // Examples:
    // 99.97% → Math.floor(99.97 * 10) / 10 → Math.floor(999.7) / 10 → 999 / 10 → 99.9%
    // 99.99% → Math.floor(99.99 * 10) / 10 → Math.floor(999.9) / 10 → 999 / 10 → 99.9%
    // 100% → Shows as 100% (exact match)
    // 45.67% → Math.floor(45.67 * 10) / 10 → Math.floor(456.7) / 10 → 456 / 10 → 45.6%
    const clampedValueString = clampedValue === 100 ? '100' : (Math.floor(clampedValue * 10) / 10).toFixed(1)

    return (
      <div
        ref={ref}
        className={clsx('w-full', className)}
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${clampedValueString}%`}
        {...props}
      >
        {((label !== undefined && label !== '') || value !== undefined) && (
          <div className="mb-1 flex justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>{label}</span>
            <span>{`${clampedValueString}%`}</span>
          </div>
        )}
        <div className={clsx('w-full bg-gray-200 rounded-full dark:bg-gray-700', sizes[size])}>
          <div
            className={clsx(
              'rounded-full transition-all duration-300 ease-out',
              colors[color],
              sizes[size],
              animated && 'animate-pulse',
            )}
            style={{ width: `${clampedValue}%` }}
          />
        </div>
      </div>
    )
  },
)

export default ProgressBar
